<template>
  <div
    class="duration-input form-control"
    :class="{ 'is-focused': isFocused, 'form-control-sm': size === 'sm', 'form-control-lg': size === 'lg' }"
  >
    <input
      v-if="showHours"
      ref="hourSeg"
      type="text"
      inputmode="numeric"
      class="di-seg"
      :class="{ 'di-hour-flex': !padHours }"
      :maxlength="2"
      :value="display.hours"
      :disabled="disabled"
      @input="onInput($event, 'hours')"
      @blur="onBlur('hours')"
      @keydown="onKeydown($event, 0)"
      @focus="onFocus($event, 0)"
      aria-label="Hours"
    />
    <span v-if="showHours" class="di-colon">:</span>

    <input
      ref="minSeg"
      type="text"
      inputmode="numeric"
      class="di-seg"
      :maxlength="2"
      :value="display.minutes"
      :disabled="disabled"
      @input="onInput($event, 'minutes')"
      @blur="onBlur('minutes')"
      @keydown="onKeydown($event, showHours ? 1 : 0)"
      @focus="onFocus($event, showHours ? 1 : 0)"
      aria-label="Minutes"
    />
    <span class="di-colon">:</span>

    <input
      ref="secSeg"
      type="text"
      inputmode="numeric"
      class="di-seg"
      :maxlength="2"
      :value="display.seconds"
      :disabled="disabled"
      @input="onInput($event, 'seconds')"
      @blur="onBlur('seconds')"
      @keydown="onKeydown($event, showHours ? 2 : 1)"
      @focus="onFocus($event, showHours ? 2 : 1)"
      aria-label="Seconds"
    />
  </div>
</template>

<script>
/**
 * DurationInput
 *
 * A segmented hh:mm:ss / h:mm:ss / mm:ss input, styled to match
 * BootstrapVue's form-control appearance, for Vue 2 + BootstrapVue 2.17.3.
 *
 * v-model binds a string:
 *   format="hhmmss" -> "02:30:45"
 *   format="hmmss"  -> "2:30:45"  (no leading zero on hours)
 *   format="mmss"   -> "30:45"
 *
 * Usage:
 *   <duration-input v-model="myValue" format="hmmss" :max-hours="23" />
 */
export default {
  name: "DurationInput",

  model: {
    prop: "value",
    event: "input"
  },

  props: {
    value: {
      type: String,
      default: ""
    },
    format: {
      type: String,
      default: "hhmmss",
      validator: (v) => ["hhmmss", "hmmss", "mmss"].includes(v)
    },
    maxHours: {
      type: Number,
      default: 99
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Mirrors BootstrapVue's form-control sizing prop, e.g. size="sm" | "lg"
    size: {
      type: String,
      default: null
    }
  },

  data() {
    const parsed = this.parseValue(this.value);
    return {
      hours: parsed.hours,
      minutes: parsed.minutes,
      seconds: parsed.seconds,
      isFocused: false,
      focusedIndex: null
    };
  },

  computed: {
    showHours() {
      return this.format !== "mmss";
    },
    padHours() {
      return this.format === "hhmmss";
    },
    display() {
      return {
        hours: this.padHours
          ? String(this.hours).padStart(2, "0")
          : String(this.hours),
        minutes: String(this.minutes).padStart(2, "0"),
        seconds: String(this.seconds).padStart(2, "0")
      };
    },
    segmentRefs() {
      const refs = [];
      if (this.showHours) refs.push("hourSeg");
      refs.push("minSeg", "secSeg");
      return refs;
    }
  },

  watch: {
    value(newVal) {
      const parsed = this.parseValue(newVal);
      this.hours = parsed.hours;
      this.minutes = parsed.minutes;
      this.seconds = parsed.seconds;
    },
    format() {
      this.emitValue();
    }
  },

  methods: {
    parseValue(str) {
      const parts = String(str || "")
        .split(":")
        .map((p) => parseInt(p, 10))
        .filter((n) => !isNaN(n));

      if (this.format === "mmss") {
        return { hours: 0, minutes: parts[0] || 0, seconds: parts[1] || 0 };
      }
      return {
        hours: parts[0] || 0,
        minutes: parts[1] || 0,
        seconds: parts[2] || 0
      };
    },

    clamp(field, n) {
      if (isNaN(n)) n = 0;
      const max = field === "hours" ? this.maxHours : 59;
      return Math.max(0, Math.min(max, n));
    },

    onInput(event, field) {
      // digits only, respect maxlength
      const cleaned = event.target.value.replace(/\D/g, "").slice(0, 2);
      event.target.value = cleaned;

      const idx = this.segmentRefs.indexOf(
        field === "hours" ? "hourSeg" : field === "minutes" ? "minSeg" : "secSeg"
      );

      if (cleaned.length >= 2 && idx < this.segmentRefs.length - 1) {
        this.focusSegment(idx + 1);
      }
    },

    onBlur(field) {
      const refName =
        field === "hours" ? "hourSeg" : field === "minutes" ? "minSeg" : "secSeg";
      const raw = this.$refs[refName].value;
      const n = this.clamp(field, parseInt(raw, 10));
      this[field] = n;
      this.isFocused = false;
      this.emitValue();
    },

    onFocus(event, idx) {
      this.isFocused = true;
      this.focusedIndex = idx;
      this.$nextTick(() => {
        if (event && event.target && event.target.select) {
          event.target.select();
        }
      });
    },

    onKeydown(event, idx) {
      const refName = this.segmentRefs[idx];
      const input = this.$refs[refName];
      const field =
        refName === "hourSeg" ? "hours" : refName === "minSeg" ? "minutes" : "seconds";

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const max = field === "hours" ? this.maxHours : 59;
        let n = parseInt(input.value || "0", 10);
        if (isNaN(n)) n = 0;
        n =
          event.key === "ArrowUp"
            ? (n + 1) % (max + 1)
            : (n - 1 + (max + 1)) % (max + 1);
        this[field] = n;
        this.emitValue();
        this.$nextTick(() => input.select());
      } else if (
        event.key === "ArrowRight" &&
        input.selectionStart === input.value.length &&
        idx < this.segmentRefs.length - 1
      ) {
        event.preventDefault();
        this.focusSegment(idx + 1);
      } else if (event.key === "ArrowLeft" && input.selectionStart === 0 && idx > 0) {
        event.preventDefault();
        this.focusSegment(idx - 1);
      } else if (event.key === ":" || event.key === " ") {
        event.preventDefault();
        if (idx < this.segmentRefs.length - 1) {
          this.focusSegment(idx + 1);
        }
      } else if (event.key === "Backspace" && input.value === "" && idx > 0) {
        event.preventDefault();
        this.focusSegment(idx - 1);
      }
    },

    focusSegment(idx) {
      const refName = this.segmentRefs[idx];
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el) {
          el.focus();
          el.select();
        }
      });
    },

    emitValue() {
      const parts = [];
      if (this.showHours) {
        parts.push(this.padHours ? String(this.hours).padStart(2, "0") : String(this.hours));
      }
      parts.push(String(this.minutes).padStart(2, "0"));
      parts.push(String(this.seconds).padStart(2, "0"));
      const val = parts.join(":");
      this.$emit("input", val);
      this.$emit("change", val);
    }
  }
};
</script>

<style scoped>
.duration-input {
  display: inline-flex;
  align-items: center;
  width: auto;
  font-variant-numeric: tabular-nums;
}

.duration-input.is-focused {
  /* Match Bootstrap 4's default focus ring on .form-control */
  color: #495057;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.di-seg {
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  padding: 0 2px;
  font: inherit;
  color: inherit;
  box-sizing: content-box;
  width: 2.2ch;
}

.di-seg.di-hour-flex {
  width: 2.2ch; /* room for 1 or 2 digits */
}

.di-seg:disabled {
  background: transparent;
  cursor: not-allowed;
}

.di-colon {
  padding: 0 1px;
  color: #6c757d;
  user-select: none;
}
</style>
